import type { IProjectRepository, IStorageFolderRepository, ITeamRepository } from "@domain/repositories";
import { TYPES } from "@utils/inversifyJS/types";
import { inject, injectable } from "inversify";
import type { EffectiveStorageContextConfiguration, PathTypeSchema, PathVisibilitySchema, StorageContextConfiguration } from "@domain/schemas/BI";

/**
Team/Project storage-configuration CRUD (WP5.5) - one service for both levels since the DTO shapes
and call patterns are identical; only the owning repository differs.
*/
@injectable()
export class StorageConfigurationService {
  @inject(TYPES.ITeamRepository)
  private _teamRepository!: ITeamRepository;

  @inject(TYPES.IProjectRepository)
  private _projectRepository!: IProjectRepository;

  @inject(TYPES.IStorageFolderRepository)
  private _storageFolderRepository!: IStorageFolderRepository;

  /**
  Level-agnostic - browses the facet's own container, not a resolved team/project path. Backs the
  "Browse" folder picker; manual path entry remains available regardless of what this returns.
  */
  async listFoldersAsync(pathType: PathTypeSchema, folder: string): Promise<string[]> {
    return this._storageFolderRepository.listFoldersAsync(pathType, folder);
  }

  async getTeamStorageConfigurationAsync(teamId: string): Promise<StorageContextConfiguration> {
    return this._teamRepository.getStorageConfigurationAsync(teamId);
  }

  async getTeamEffectiveStorageConfigurationAsync(teamId: string): Promise<EffectiveStorageContextConfiguration> {
    return this._teamRepository.getEffectiveStorageConfigurationAsync(teamId);
  }

  async addTeamPathAsync(teamId: string, pathType: PathTypeSchema, path: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    return this._teamRepository.addStoragePathAsync(teamId, pathType, path, visibility);
  }

  async removeTeamPathAsync(teamId: string, pathId: string): Promise<StorageContextConfiguration> {
    return this._teamRepository.removeStoragePathAsync(teamId, pathId);
  }

  async setTeamPathVisibilityAsync(teamId: string, pathId: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    return this._teamRepository.setStoragePathVisibilityAsync(teamId, pathId, visibility);
  }

  async setTeamPathValueAsync(teamId: string, pathId: string, path: string): Promise<StorageContextConfiguration> {
    return this._teamRepository.setStoragePathValueAsync(teamId, pathId, path);
  }

  async setTeamResultsPathAsync(teamId: string, resultsPath: string): Promise<StorageContextConfiguration> {
    return this._teamRepository.setStorageResultsPathAsync(teamId, resultsPath);
  }

  async getProjectStorageConfigurationAsync(projectId: string): Promise<StorageContextConfiguration> {
    return this._projectRepository.getStorageConfigurationAsync(projectId);
  }

  async getProjectEffectiveStorageConfigurationAsync(projectId: string): Promise<EffectiveStorageContextConfiguration> {
    return this._projectRepository.getEffectiveStorageConfigurationAsync(projectId);
  }

  async addProjectPathAsync(projectId: string, pathType: PathTypeSchema, path: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    return this._projectRepository.addStoragePathAsync(projectId, pathType, path, visibility);
  }

  async removeProjectPathAsync(projectId: string, pathId: string): Promise<StorageContextConfiguration> {
    return this._projectRepository.removeStoragePathAsync(projectId, pathId);
  }

  async setProjectPathVisibilityAsync(projectId: string, pathId: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    return this._projectRepository.setStoragePathVisibilityAsync(projectId, pathId, visibility);
  }

  async setProjectPathValueAsync(projectId: string, pathId: string, path: string): Promise<StorageContextConfiguration> {
    return this._projectRepository.setStoragePathValueAsync(projectId, pathId, path);
  }

  async setProjectResultsPathAsync(projectId: string, resultsPath: string): Promise<StorageContextConfiguration> {
    return this._projectRepository.setStorageResultsPathAsync(projectId, resultsPath);
  }
}
